import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  TextField,
  CircularProgress,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker as MuiDatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import DatePickerNewLedger from "../../assets/DatePickerNewLedger.svg";
import moment, { Moment } from "moment";
import { useNavigate } from "react-router-dom";
import { useForm, Controller, useWatch } from "react-hook-form";
import Grid from "@mui/material/Grid2";
import {
  deleteCreditNote,
  downloadCreditNote,
  previewCreditNote,
  getInvoiceFromOrders,
} from "../../api/generateCreditNoteAPI";
import { getLedgerList } from "../../api/ledgerAPI";
import {
  creditDialogStyles,
  Generate,
  labelTypography,
  dialogActionsStyles,
  DeleteButton,
  SaveButton,
} from "./LedgerModal.styles";
import Toast from "../../common/Toast";
import CustomMultiSelect from "./CustomMultiSelect";

type Props = {
  open: boolean;
  onClose: () => void;
  customerId: string;
  mode?: "create" | "edit";
  creditNoteData?: {
    creditNoteNo: string;
    date: string;
    credit?: string | number;
  };
  fetchLedgerData: (page: string, limit: string) => void;
  rowsPerPage: number;
};

type FormValues = {
  creditNoteNo: string;
  date: Moment | null;
  credit?: string | number;
  orderIds: string[];
  invoiceNos: string[];
  creditAmount: string;
};

const CreditNoteModal: React.FC<Props> = ({
  open,
  onClose,
  customerId,
  mode = "create",
  creditNoteData,
  fetchLedgerData,
  rowsPerPage,
}) => {
  const navigate = useNavigate();
  const orgId = localStorage.getItem("orgId") ?? "";
  const [loading, setLoading] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [isFetchingId, setIsFetchingId] = useState(false);
  const [isError, setIsError] = useState(false);
  const [invoiceData, setInvoiceData] = useState<any[]>([]);
  const [orderIds, setOrderIds] = useState<any[]>([]);
  const [isFetchingInvoices, setIsFetchingInvoices] = useState(false);
  const [ledgerEntries, setLedgerEntries] = useState<any[]>([]);

  useEffect(() => {
    if (open && mode === "create" && customerId) {
      setIsFetchingId(true);
      const today = moment().format("YYYY-MM-DD");
      previewCreditNote(customerId, { date: today })
        .then((res) => {
          const generatedId = res?.data?.id || res?.data?.creditNoteNo;
          if (generatedId) {
            reset((prev) => ({
              ...prev,
              creditNoteNo: generatedId,
              date: moment(),
              orderIds: prev?.orderIds ?? [],
              invoiceNos: prev?.invoiceNos ?? [],
              creditAmount: prev?.creditAmount ?? "",
            }));
          }
        })
        .catch((err) => {
          console.error("Failed to fetch generated credit note ID", err);
        })
        .finally(() => setIsFetchingId(false));

      // Fetch all DEBIT order IDs from ledger (use a large limit to get all pages)
      getLedgerList({
        customerId,
        page: "1",
        limit: "10000",
        sort: "desc",
        startDate: "",
        endDate: "",
        search: "",
      })
        .then((res: any) => {
          const entries: any[] = res?.data ?? [];
          setLedgerEntries(entries);
          const debitOrderIds = Array.from(
            new Set(
              entries
                .filter((e) => e?.type === "DEBIT" && e?.id)
                .map((e) => e.id as string)
            )
          ).map((orderId) => ({ orderId }));
          setOrderIds(debitOrderIds);
        })
        .catch((err) => {
          console.error("Failed to fetch ledger order IDs", err);
        });
    }
  }, [open, mode, customerId]);

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    defaultValues: {
      creditNoteNo: creditNoteData?.creditNoteNo || "",
      date: creditNoteData?.date ? moment(creditNoteData.date) : null,
      credit: creditNoteData?.credit || "",
      orderIds: [],
      invoiceNos: [],
      creditAmount: "",
    },
  });

  const selectedOrderIds = useWatch({ control, name: "orderIds" }) || [];

  const transformInvoices = (raw: any[]): any[] => {
    const transformedInvoices: any[] = [];
    const seen = new Set<string>();
    const pushInvoice = (
      invoiceNo: string,
      orderId: string,
      type: "Excise" | "Service"
    ) => {
      if (!invoiceNo || !orderId) return;
      const key = `${orderId}|${invoiceNo}`;
      if (seen.has(key)) return;
      seen.add(key);
      transformedInvoices.push({ invoiceNo, orderId, type });
    };
    raw.forEach((invoice: any) => {
      const orderId = invoice?.orderId ?? invoice?.id;
      const excise = invoice?.exciseID ?? invoice?.exciseId;
      const service = invoice?.serviceID ?? invoice?.serviceId;
      const exciseList = invoice?.exciseIDs ?? invoice?.exciseIds;
      const serviceList = invoice?.serviceIDs ?? invoice?.serviceIds;
      if (Array.isArray(exciseList)) {
        exciseList.forEach((id: string) => pushInvoice(id, orderId, "Excise"));
      } else if (excise) {
        pushInvoice(excise, orderId, "Excise");
      }
      if (Array.isArray(serviceList)) {
        serviceList.forEach((id: string) =>
          pushInvoice(id, orderId, "Service")
        );
      } else if (service) {
        pushInvoice(service, orderId, "Service");
      }
    });
    return transformedInvoices;
  };

  React.useEffect(() => {
    if (!open || mode !== "create" || !customerId) return;
    if (selectedOrderIds.length === 0) {
      setInvoiceData([]);
      return;
    }

    // Derive invoice numbers from CREDIT_NOTE entries in the already-fetched ledger data
    const seenLedger = new Set<string>();
    const ledgerDerived: any[] = [];
    ledgerEntries
      .filter((e) => e?.type === "CREDIT_NOTE" && Array.isArray(e?.orderIds))
      .forEach((entry) => {
        const matchingOrderId = entry.orderIds.find((oid: string) =>
          selectedOrderIds.includes(oid)
        );
        if (!matchingOrderId) return;
        (entry.invoiceNos || []).forEach((invoiceNo: string) => {
          if (!invoiceNo || seenLedger.has(invoiceNo)) return;
          seenLedger.add(invoiceNo);
          const type = invoiceNo.startsWith("EXC") ? "Excise" : "Service";
          ledgerDerived.push({ invoiceNo, orderId: matchingOrderId, type });
        });
      });

    const orderIdsSnapshot = [...selectedOrderIds];
    setIsFetchingInvoices(true);
    getInvoiceFromOrders(customerId, orderIdsSnapshot)
      .then((res) => {
        const raw =
          (Array.isArray(res) && res) ||
          (Array.isArray(res?.data) && res.data) ||
          (Array.isArray(res?.data?.response) && res.data.response) ||
          (Array.isArray(res?.data?.data) && res.data.data) ||
          [];
        const apiInvoices = transformInvoices(raw).filter((inv) =>
          orderIdsSnapshot.includes(inv.orderId)
        );
        // Merge: API results first, then ledger-derived (skip duplicates by invoiceNo)
        const combined = [...apiInvoices];
        ledgerDerived.forEach((inv) => {
          if (!combined.some((c) => c.invoiceNo === inv.invoiceNo)) {
            combined.push(inv);
          }
        });
        setInvoiceData(combined);
      })
      .catch((err) => {
        console.error("Failed to fetch invoice data", err);
        // Fall back to ledger-derived invoices if API fails
        setInvoiceData(ledgerDerived);
        setToastMessage("Failed to fetch invoice data");
        setIsError(true);
        setShowToast(true);
      })
      .finally(() => setIsFetchingInvoices(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrderIds, ledgerEntries, open, mode, customerId]);

  const filteredInvoiceData = invoiceData;

  const selectedInvoiceNos = useWatch({ control, name: "invoiceNos" }) || [];
  React.useEffect(() => {
    if (!selectedInvoiceNos || selectedInvoiceNos.length === 0) return;
    const validInvoiceNos = invoiceData.map((inv: any) => inv.invoiceNo);
    const pruned = selectedInvoiceNos.filter((no) =>
      validInvoiceNos.includes(no)
    );
    if (pruned.length !== selectedInvoiceNos.length) {
      reset((prev) => ({ ...prev, invoiceNos: pruned }), {
        keepDirty: true,
        keepTouched: true,
        keepErrors: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceData]);

  React.useEffect(() => {
    if (mode === "edit" && creditNoteData) {
      reset({
        creditNoteNo: creditNoteData.creditNoteNo,
        date: moment(creditNoteData.date),
        orderIds: [],
        invoiceNos: [],
        creditAmount: "",
      });
    }
  }, [mode, creditNoteData, reset]);

  const onSubmit = async (data: FormValues) => {
    if (!data.date) return;
    setLoading(true);

    try {
      const creditAmountNum = data.creditAmount ? Number(data.creditAmount) : 0;

      const payload: Record<string, any> = {
        date: data.date.format("YYYY-MM-DD"),
        id: data.creditNoteNo.trim(),
        orderIds: data.orderIds,
        invoiceNos: data.invoiceNos,
        creditAmount: creditAmountNum,
      };

      const res = await previewCreditNote(customerId, payload);
      const serverPreview = res?.data || {};

      const previewData = {
        ...serverPreview,
        creditNoteNo: data.creditNoteNo.trim() || serverPreview?.id,
        date: data.date.format("YYYY-MM-DD"),
        orderIds: data.orderIds,
        invoiceNos: data.invoiceNos,
        creditAmount: creditAmountNum,
        preTaxTotal: creditAmountNum,
        grandTotal: creditAmountNum,
      };

      onClose();
      navigate(`/maltora-seller/ledgers/credit-note/${customerId}`, {
        state: { previewData },
      });
    } catch (err: any) {
      const msg =
        err?.response?.data?.error?.type || "Failed to preview credit note";

      setToastMessage(msg);
      setIsError(true);
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCreditNoteClick = async (noteId: string) => {
    try {
      const url = await downloadCreditNote(noteId);
      if (url) {
        window.open(url, "_blank");
      } else {
        setToastMessage("Credit note URL not available");
        setIsError(true);
        setShowToast(true);
      }
    } catch (err) {
      console.error("Failed to fetch credit note receipt", err);
      setToastMessage("Failed to fetch credit note. Please try again.");
      setIsError(true);
      setShowToast(true);
    }
  };

  const handleDeleteCreditNote = async () => {
    if (!creditNoteData?.creditNoteNo) return;

    setLoading(true);
    try {
      await deleteCreditNote(creditNoteData.creditNoteNo);

      setToastMessage("Credit note deleted successfully");
      setIsError(false);
      setShowToast(true);

      onClose();
      fetchLedgerData("1", rowsPerPage.toString());
    } catch (err) {
      console.error("Failed to delete credit note", err);
      setToastMessage("Failed to delete credit note. Please try again.");
      setIsError(true);
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            padding: "24px",
            maxWidth: "600px",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontSize: "18px",
            fontWeight: 600,
            color: "#1D2939",
          }}
        >
          {mode === "edit" ? "Edit Credit Note" : "Credit Note"}

          <IconButton onClick={onClose} size="small">
            <CloseIcon sx={{ fontSize: "20px" }} />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Date */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="body2" sx={labelTypography}>
                Choose Date
              </Typography>
              <Controller
                name="date"
                control={control}
                rules={{ required: "Date is required" }}
                render={({ field }) => (
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <MuiDatePicker
                      value={field.value}
                      onChange={field.onChange}
                      format="DD/MM/YYYY"
                      maxDate={moment()}
                      disabled={mode === "edit"}
                      open={isDatePickerOpen}
                      onOpen={() => setIsDatePickerOpen(true)}
                      onClose={() => setIsDatePickerOpen(false)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          placeholder: "Select date",
                          error: !!errors.date,
                          helperText: errors.date?.message,
                          sx: {
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "6px",
                              height: "44px",
                              fontSize: "14px",
                            },
                            "& .MuiInputBase-input": {
                              fontSize: "14px",
                              padding: "12px 14px",
                            },
                          },
                          InputProps: {
                            endAdornment: (
                              <IconButton
                                onClick={() => setIsDatePickerOpen(true)}
                                disabled={mode === "edit"}
                                sx={{ padding: "4px" }}
                              >
                                <img
                                  src={DatePickerNewLedger}
                                  alt="Calendar Icon"
                                  width={20}
                                  height={20}
                                />
                              </IconButton>
                            ),
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                )}
              />
            </Grid>

            {/* Credit Note ID */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="body2" sx={labelTypography}>
                Credit Note ID
              </Typography>
              <Controller
                name="creditNoteNo"
                control={control}
                rules={{}}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    placeholder="CN/SER/26-12025"
                    error={!!errors.creditNoteNo}
                    helperText={errors.creditNoteNo?.message}
                    inputProps={{ maxLength: 20 }}
                    disabled={true}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "6px",
                        height: "44px",
                        fontSize: "14px",
                      },
                      "& .MuiInputBase-input": {
                        fontSize: "14px",
                        padding: "12px 14px",
                      },
                    }}
                    InputProps={{
                      endAdornment: isFetchingId ? (
                        <CircularProgress size={16} />
                      ) : null,
                    }}
                  />
                )}
              />
            </Grid>

            {mode !== "edit" && (
              <>
                {/* Order ID */}
                <Grid size={{ xs: 12 }}>
                  <Controller
                    name="orderIds"
                    control={control}
                    rules={{ required: "Order ID is required" }}
                    render={({ field }) => (
                      <CustomMultiSelect
                        label="Order ID"
                        placeholder="Select order ID"
                        options={orderIds.map((order) => ({
                          value: order.orderId,
                          label: order.orderId,
                        }))}
                        value={field.value}
                        onChange={field.onChange}
                        error={!!errors.orderIds}
                      />
                    )}
                  />
                </Grid>

                {/* Towards Invoice No */}
                <Grid size={{ xs: 12 }}>
                  <Controller
                    name="invoiceNos"
                    control={control}
                    rules={{ required: "Invoice number is required" }}
                    render={({ field }) => (
                      <CustomMultiSelect
                        label="Towards Invoice No."
                        placeholder="Select invoice number"
                        options={filteredInvoiceData.map((invoice) => ({
                          value: invoice.invoiceNo,
                          label: invoice.invoiceNo,
                          subLabel: `Order: ${invoice.orderId}`,
                          type: invoice.type?.toLowerCase() as
                            | "excise"
                            | "service",
                        }))}
                        value={field.value}
                        onChange={field.onChange}
                        error={!!errors.invoiceNos}
                        showTypeBadge={true}
                      />
                    )}
                  />
                </Grid>

                {/* Credit Amount */}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="body2" sx={labelTypography}>
                    Credit Amount
                  </Typography>
                  <Controller
                    name="creditAmount"
                    control={control}
                    rules={{}}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="Enter credit amount"
                        error={!!errors.creditAmount}
                        helperText={errors.creditAmount?.message}
                        type="number"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "6px",
                            height: "44px",
                            fontSize: "14px",
                          },
                          "& .MuiInputBase-input": {
                            fontSize: "14px",
                            padding: "12px 14px",
                          },
                          // Hide spinner arrows for number input
                          '& input[type="number"]::-webkit-inner-spin-button': {
                            WebkitAppearance: "none",
                            margin: 0,
                          },
                          '& input[type="number"]::-webkit-outer-spin-button': {
                            WebkitAppearance: "none",
                            margin: 0,
                          },
                          '& input[type="number"]': {
                            MozAppearance: "textfield",
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>

        <DialogActions
          sx={{
            ...dialogActionsStyles,
            justifyContent: "center",
            gap: "16px",
            paddingTop: "24px",
            paddingBottom: "32px",
          }}
        >
          {mode === "edit" ? (
            <>
              <DeleteButton variant="outlined" onClick={handleDeleteCreditNote}>
                Delete
              </DeleteButton>
              <SaveButton
                onClick={() => {
                  if (creditNoteData?.creditNoteNo) {
                    handleCreditNoteClick(creditNoteData.creditNoteNo);
                  }
                  onClose();
                }}
                variant="contained"
                disabled={loading}
              >
                Preview
              </SaveButton>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                onClick={onClose}
                sx={{
                  width: "120px",
                  height: "44px",
                  borderRadius: "6px",
                  border: "1px solid #D0D5DD",
                  color: "#344054",
                  fontSize: "14px",
                  fontWeight: "500",
                  textTransform: "none",
                  backgroundColor: "#FFFFFF",
                  "&:hover": {
                    border: "1px solid #D0D5DD",
                    backgroundColor: "#F8F9FA",
                  },
                }}
              >
                Cancel
              </Button>
              <Generate
                onClick={handleSubmit(onSubmit)}
                variant="contained"
                disabled={loading || isFetchingId}
                sx={{
                  width: "180px",
                  height: "44px",
                  borderRadius: "6px",
                  fontSize: "14px",
                  fontWeight: "500",
                  textTransform: "none",
                  backgroundColor: "#FF6B00",
                  "&:hover": {
                    backgroundColor: "#FF8C42",
                  },
                  "&:disabled": {
                    backgroundColor: "#F3F4F6",
                    color: "#9CA3AF",
                  },
                }}
              >
                Generate Receipt
              </Generate>
            </>
          )}
        </DialogActions>
      </Dialog>
      <Toast
        showToast={showToast}
        setShowToast={setShowToast}
        toastMessage={toastMessage}
        error={isError}
      />
    </>
  );
};
export default CreditNoteModal;
